import { useAuth } from "@clerk/react";
import { useEffect, useState } from "react";
import type { User } from "@shared/types";
import { apiRequest } from "@/features/api/apiClient";

type CurrentUserState = {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

export function useCurrentUser(): CurrentUserState {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function loadUser() {
    if (!isLoaded) {
      return;
    }

    if (!isSignedIn) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await apiRequest<{ user: User }>("/api/me", { token });
      setUser(response.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load user");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    void loadUser();
  }, [isLoaded, isSignedIn]);

  return {
    user,
    isLoading: !isLoaded || isLoading,
    error,
    refresh: loadUser,
  };
}
